import React, { useEffect, useState } from "react"
import ProfileTabs from "../components/ProfileTabs"
import DialogPaymentForm from "../components/dialogs/DialogPaymentForm";
import { useAtom } from "jotai";
import { showAddPaymentMethodAtom, userAtom } from "../store";
import { updateUserProfile } from "../firebase/users";
import { useAuth } from "../context/authContext";
import axios from "axios";
import DialogWithdraw from "../components/dialogs/DialogWithdraw";
import ProfileTitle from "../components/ProfileTitle";
import { getWithdrawsByUserId } from "../firebase/payments";
import { createWithdrawalSession, checkWithdrawalLimits, updateAccountCapabilities } from "../firebase/payments";
import { getAuthHeaders } from "../firebase/authHeaders";
import { formatDate } from '../utils'
import Pagination from '../components/commons/Pagination'
import DialogLoading from "../components/dialogs/DialogLoading";
import WithdrawalAmountSelector from "../components/commons/WithdrawalAmountSelector";

const PaymentSettingsPage = () => {
    const [showPaymentMethod, setShowPaymentMethod] = useAtom(showAddPaymentMethodAtom)
    const [showWithdrawModal, setShowWithdrawModal] = useState(false)
    const [userData, setUserData] = useAtom(userAtom)
    const { getUpdatedUserData, user } = useAuth()
    const [loading, setLoading] = useState(false)
    const [withdraws, setWithdraws] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [withdrawalLimits, setWithdrawalLimits] = useState(null)
    const [withdrawalAmount, setWithdrawalAmount] = useState(0);
    const [withdrawalMethod, setWithdrawalMethod] = useState('stripe'); // 'stripe' or 'paypal'
    
    // Debug withdrawal amount changes
    const handleWithdrawalAmountChange = (newAmount) => {
        console.log('Setting withdrawal amount to:', newAmount);
        setWithdrawalAmount(newAmount);
    };
    const [destinationAccount, setDestinationAccount] = useState('')
    const [showWithdrawalForm, setShowWithdrawalForm] = useState(false)
    const [loadingLimits, setLoadingLimits] = useState(false)

    useEffect(() => {
        const checkAccountStatus = async () => {
            if (userData && !userData.stripe_active && userData.stripe_id) {
                try {
                    const response = await axios({
                        method: 'post',
                        url: process.env.REACT_APP_PAYMENT_API + '/check-account-active',
                        headers: await getAuthHeaders(),
                        data: {
                            stripeId: userData.stripe_id
                        }
                    });
                    
                    console.log('Account status check response:', response.data);
                    
                    if (response.data.isActive) {
                        await updateUserProfile(userData.id, { stripe_active: response.data.isActive });
                        await getUpdatedUserData();
                        console.log('✅ Account automatically activated!');
                    } else {
                        console.log('Account not yet active:', response.data.message || 'Incomplete onboarding');
                    }
                } catch (error) {
                    console.error('Error checking account status:', error);
                }
            }
        };

        if (userData) {
            checkAccountStatus();

            getWithdrawsByUserId(userData.id).then(data => {
                setWithdraws(data)
            })

            // Load withdrawal limits
            if (user) {
                loadWithdrawalLimits()
            }
        }

        // Handle withdrawal success/cancel from Stripe
        const urlParams = new URLSearchParams(window.location.search);
        const withdrawalSuccess = urlParams.get('withdrawal_success');
        const withdrawalCancelled = urlParams.get('withdrawal_cancelled');
        const withdrawalAmount = urlParams.get('amount');

        if (withdrawalSuccess === 'true') {
            alert(`Withdrawal session created successfully! Amount: $${withdrawalAmount}`);
            // Refresh user data
            getUpdatedUserData();
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (withdrawalCancelled === 'true') {
            alert('Withdrawal was cancelled.');
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [userData, user])

    const loadWithdrawalLimits = async () => {
        try {
            setLoadingLimits(true)
            const response = await checkWithdrawalLimits(user.uid)
            
            if (response.success) {
                setWithdrawalLimits(response.limits)
            } else {
                console.error('Failed to load withdrawal limits:', response.message)
                // Set default limits as fallback
                setWithdrawalLimits({
                    status: 'unrestricted',
                    message: 'Default withdrawal limits applied',
                    maxWithdrawalAmount: 10000,
                    minWithdrawalAmount: 10,
                    currentBalance: userData?.balance || 0,
                    withdrawalNumber: 1,
                    totalWithdrawals: 0,
                    remainingDays: 0,
                    firstWithdrawalDate: null,
                    isNewUser: false
                })
            }
        } catch (error) {
            console.error('Error loading withdrawal limits:', error)
            // Set default limits as fallback
            setWithdrawalLimits({
                status: 'unrestricted',
                message: 'Default withdrawal limits applied',
                maxWithdrawalAmount: 10000,
                minWithdrawalAmount: 10,
                currentBalance: userData?.balance || 0,
                withdrawalNumber: 1,
                totalWithdrawals: 0,
                remainingDays: 0,
                firstWithdrawalDate: null,
                isNewUser: false
            })
        } finally {
            setLoadingLimits(false)
        }
    }

    const getCurrentWithdrawalLimit = () => {
        if (withdrawalLimits) {
            const limit = Math.min(withdrawalLimits.maxWithdrawalAmount, withdrawalLimits.currentBalance);
            console.log('Withdrawal limits:', withdrawalLimits);
            console.log('Current withdrawal limit:', limit);
            return limit;
        }
        const balance = userData?.balance || 0;
        console.log('No withdrawal limits, using balance:', balance);
        return balance;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'new_user_restricted': return 'bg-yellow-500'
            case 'waiting_period': return 'bg-orange-500'
            case 'unrestricted': return 'bg-green-500'
            default: return 'bg-gray-500'
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'new_user_restricted': return '⚠️'
            case 'waiting_period': return '⏳'
            case 'unrestricted': return '✅'
            default: return 'ℹ️'
        }
    }

    const removeCard = async () => {
        const newPayment = { ...userData.payment }
        delete newPayment.card
        await updateUserProfile(userData.id, { ...userData, payment: newPayment })
        await getUpdatedUserData()
    }

    const removeBank = async () => {
        const newPayment = { ...userData.payment }
        delete newPayment.bank

        axios({
            method: 'post',
            url: process.env.REACT_APP_PAYMENT_API + '/remove-bank-account',
            headers: await getAuthHeaders(),
            data: {
                bankAccountId: userData.payment.bank.id,
                customerId: userData.payment.customer_id
            }
        }).then(async () => {
        }).catch((error) => {
        })
        await updateUserProfile(userData.id, { ...userData, payment: newPayment })
        await getUpdatedUserData()
    }

    const activeCard = async () => {
        setLoading(true);
        axios({
            method: 'post',
            url: process.env.REACT_APP_PAYMENT_API + '/create-account',
            headers: await getAuthHeaders(),
            data: {
                prevLink: window.location.href
            }
        }).then(async ({ data }) => {
            await updateUserProfile(userData.id, { stripe_id: data.accountId, stripe_active: false })
            setLoading(false)
            window.location.href = data.accountLink;
        })
    }

    const handleWithdraw = async () => {
        console.log('Withdraw button clicked');
        console.log('Withdrawal amount:', withdrawalAmount);
        console.log('User ID:', user?.uid);
        console.log('Withdrawal method:', withdrawalMethod);
        
        if (withdrawalAmount <= 0) {
            alert("Withdrawal amount must be over 0!")
            return
        }

        if (withdrawalMethod === 'paypal' && !destinationAccount) {
            alert("Please enter your PayPal email address!")
            return
        }

        try {
            setLoading(true)
            console.log('Processing withdrawal...');
            
            if (withdrawalMethod === 'paypal') {
                // PayPal withdrawal
                const response = await axios.post(process.env.REACT_APP_PAYMENT_API + '/paypal-withdraw', {
                    paypalEmail: destinationAccount,
                    amount: withdrawalAmount,
                    userId: user.uid
                }, { headers: await getAuthHeaders() });
                
                if (response.data.success) {
                    console.log('PayPal withdrawal successful:', response.data);
                    alert(`✅ PayPal withdrawal successful! $${withdrawalAmount} has been sent to your PayPal account.`);
                    
                    // Reset form
                    setWithdrawalAmount(0);
                    setDestinationAccount('');
                    setShowWithdrawalForm(false);
                    
                    // Reload withdrawal limits
                    await loadWithdrawalLimits();
                    
                    // Refresh user data
                    await getUpdatedUserData();
                } else {
                    alert(response.data.message || response.data.error || 'PayPal withdrawal failed');
                }
            } else {
                // Stripe withdrawal (existing method)
                const response = await createWithdrawalSession(
                    withdrawalAmount, 
                    user.uid, 
                    window.location.href
                )
                
                console.log('Withdrawal response:', response);
                
                if (response.success) {
                    console.log('Withdrawal successful:', response);
                    alert(`✅ Stripe withdrawal successful! $${withdrawalAmount} has been transferred to your bank account.`);
                    
                    // Refresh user data to show updated balance
                    if (userData) {
                        // Update local user data
                        const updatedUserData = {
                            ...userData,
                            balance: response.newBalance
                        };
                        // You might need to update your global user state here
                    }
                    
                    // Reset form
                    setWithdrawalAmount(0);
                    setShowWithdrawalForm(false);
                    
                    // Reload withdrawal limits
                    await loadWithdrawalLimits();

                    // Refresh user data
                    await getUpdatedUserData();
                    
                } else {
                    console.error('Withdrawal failed:', response);
                    
                    // Handle transfer capability error
                    if (response.error && response.error.includes('transfers')) {
                        const updateAccount = confirm(
                            'Your Stripe account needs to be updated to enable transfers. Would you like to update your account now?'
                        );
                        
                        if (updateAccount) {
                            try {
                                const updateResponse = await updateAccountCapabilities(
                                    userData.stripe_id, 
                                    window.location.href
                                );
                                
                                if (updateResponse.success) {
                                    alert('Please complete the account update process to enable withdrawals.');
                                    window.location.href = updateResponse.accountLink;
                                } else {
                                    alert('Failed to update account. Please contact support.');
                                }
                            } catch (error) {
                                console.error('Error updating account:', error);
                                alert('Failed to update account. Please contact support.');
                            }
                        }
                    } else {
                        alert(response.message || response.error || 'Failed to process withdrawal')
                    }
                }
            }
        } catch (error) {
            console.error("Withdrawal error:", error)
            alert('Error processing withdrawal')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            {loading &&
                <DialogLoading
                    open={true}
                    onClose={() => { }}
                    type="loading"
                    title="Please wait"
                    message="We are processing your booking request. Please wait and don't close this page"
                    buttonText="Cancel"
                    onButtonClick={() => { }}
                />
            }
            <ProfileTitle />

            <ProfileTabs activeTab="Payment Settings" />

            <span className="block uppercase text-primary text-[12px] max-lg:mb-8">Payment Settings</span>

            {/* Withdrawal Status Summary */}
            {loadingLimits ? (
                <div className="mb-6 border-[1px] border-gray p-4 rounded-lg">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        <span className="ml-3 text-white">Loading withdrawal status...</span>
                    </div>
                </div>
            ) : withdrawalLimits && (
                <div className="mb-6 border-[1px] border-gray p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <span className="text-2xl">{getStatusIcon(withdrawalLimits.status)}</span>
                            <div>
                                <h3 className="text-lg font-semibold text-white">
                                    Withdrawal Status: {withdrawalLimits.status.replace('_', ' ').toUpperCase()}
                                </h3>
                                <p className="text-gray-300 text-sm">{withdrawalLimits.message}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-white">${withdrawalLimits.currentBalance.toFixed(2)}</div>
                            <div className="text-gray-400 text-sm">Available Balance</div>
                        </div>
                    </div>
                    
                    {withdrawalLimits.remainingDays > 0 && (
                        <div className="mt-3 p-2 bg-orange-900/20 border border-orange-500/30 rounded">
                            <p className="text-orange-300 text-sm text-center">
                                ⏰ {withdrawalLimits.remainingDays} days remaining until unlimited withdrawals
                            </p>
                        </div>
                    )}
                </div>
            )}

            <DialogPaymentForm
                open={showPaymentMethod}
                onClose={() => setShowPaymentMethod(false)}
            />

            <DialogWithdraw
                open={showWithdrawModal}
                onClose={() => setShowWithdrawModal(false)}
            />

            <div className="mt-7 grid lg:grid-cols-12 grid-cols-1 w-full gap-4 mb-4">
                <div className="border-[1px] border-gray p-4 w-full h-fit lg:col-span-4">
                    <div className="text-[#8F8F8F] uppercase text-xs">Available Balance</div>
                    <div className="text-[30px] text-white font-bold mt-[14px]">${(userData.balance-userData.pendingBalance).toFixed(2)}</div>

                    <hr className="border-gray mt-3" />
                    <div className="mt-4">
                        <button
                            className={`solo-button text-black bg-white w-full py-2 uppercase font-semibold text-sm ${!userData.stripe_active ? 'opacity-50' : ''}`}
                            disabled={false} // Temporarily remove disabled condition
                            onClick={() => {
                                console.log('Withdraw Funds button clicked!');
                                console.log('userData.stripe_active:', userData.stripe_active);
                                console.log('showWithdrawalForm:', showWithdrawalForm);
                                setShowWithdrawalForm(!showWithdrawalForm);
                            }}
                        >
                            {showWithdrawalForm ? 'Cancel Withdrawal' : 'Withdraw Funds'}
                        </button>
                        <div className="text-xs text-gray-400 mt-1">
                    
                        </div>
                    </div>

                    {/* Withdrawal Form */}
                    {showWithdrawalForm && (
                        <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                            <h4 className="text-white font-semibold mb-3">Withdrawal Details</h4>
                            
                            {withdrawalLimits ? (
                                <WithdrawalAmountSelector
                                    amount={withdrawalAmount}
                                    setAmount={handleWithdrawalAmountChange}
                                    maxAmount={withdrawalLimits.maxWithdrawalAmount}
                                    minAmount={withdrawalLimits.minWithdrawalAmount}
                                    currentBalance={withdrawalLimits.currentBalance}
                                    onAmountChange={(newAmount) => handleWithdrawalAmountChange(newAmount)}
                                />
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                        {[25, 50, 100, 250, 500, 1000].map((amount) => (
                                            <button
                                                key={amount}
                                                type="button"
                                                onClick={() => handleWithdrawalAmountChange(amount)}
                                                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                    withdrawalAmount === amount
                                                        ? 'bg-blue-500 text-white border-blue-500'
                                                        : 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600'
                                                }`}
                                            >
                                                ${amount}
                                            </button>
                                        ))}
                                        
                                        <button
                                            type="button"
                                            onClick={() => handleWithdrawalAmountChange(userData?.balance || 0)}
                                            className="px-3 py-2 rounded-lg border border-green-600 text-green-400 hover:bg-green-600 hover:text-white text-sm font-medium transition-colors"
                                        >
                                            MAX
                                        </button>
                                    </div>

                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Custom Amount
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                                $
                                            </span>
                                            <input
                                                type="number"
                                                value={withdrawalAmount}
                                                onChange={(e) => handleWithdrawalAmountChange(parseFloat(e.target.value) || 0)}
                                                placeholder="Enter amount"
                                                min={10}
                                                max={10000}
                                                step="0.01"
                                                className="w-full pl-8 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div className="mt-2 text-xs text-gray-400">
                                            Min: $10 | Max: $10,000 | Available: ${userData ? (userData.balance-userData.pendingBalance).toFixed(2) : 0}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Withdrawal Method Selection */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Withdrawal Method
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="withdrawalMethod"
                                            value="stripe"
                                            checked={withdrawalMethod === "stripe"}
                                            onChange={(e) => setWithdrawalMethod(e.target.value)}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-white">Stripe Transfer</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="withdrawalMethod"
                                            value="paypal"
                                            checked={withdrawalMethod === "paypal"}
                                            onChange={(e) => setWithdrawalMethod(e.target.value)}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-white">PayPal Payout</span>
                                    </label>
                                </div>
                            </div>

                            {/* Destination Account Input */}
                            {withdrawalMethod === 'paypal' && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        PayPal Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={destinationAccount}
                                        onChange={(e) => setDestinationAccount(e.target.value)}
                                        placeholder="Enter your PayPal email"
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            )}

                            <button
                                onClick={handleWithdraw}
                                disabled={(() => {
                                    const isDisabled = loading || withdrawalAmount <= 0 || withdrawalAmount > getCurrentWithdrawalLimit();
                                    console.log('Button disabled check:', {
                                        loading,
                                        withdrawalAmount,
                                        currentLimit: getCurrentWithdrawalLimit(),
                                        isDisabled
                                    });
                                    return isDisabled;
                                })()}
                                className={`w-full mt-4 py-2 rounded-lg font-semibold transition-colors ${
                                    loading || withdrawalAmount <= 0 || withdrawalAmount > getCurrentWithdrawalLimit()
                                        ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                            >
                                {loading ? 'Processing Withdrawal...' : 'Process Withdrawal'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="md:px-7 px-4 py-4 border-[1px] lg:col-span-8 border-gray h-fit">
                    <div className="text-white text-base">
                        Withdraw Methods
                    </div>

                    <div className="flex justify-between mt-4 mb-4">
                        <span className="text-white text-sm font-light">Methods</span>
                    </div>

                    <div className="flex flex-col gap-y-4">
                        {
                            userData &&
                            <div className="border-[1px] border-gray p-2 rounded-[6px]">
                                <div className="flex items-center">
                                    <div className="flex justify-center items-center border-[1px] border-gray w-[90px] h-[45px] mr-3 rounded-[4px] overflow-hidden">
                                        <img src="/assets/icons/icon-stripe.svg" alt="visa" />
                                    </div>

                                    <div className="text-sm text-white">
                                        <div>
                                            Stripe Withdraws
                                        </div>

                                        {userData.stripe_active ? (
                                            <div className="font-thin text-[#43D633]">
                                                Account Active
                                            </div>
                                        ) : (
                                            <div className="font-thin text-gray">
                                                Account Inactive
                                                {userData.stripe_id && (
                                                    <div className="text-xs text-[#ff6b35] mt-1">
                                                        Requirements past due
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ml-auto mr-4">
                                        {
                                            !userData.stripe_active ? (
                                                <div className="flex gap-2">
                                                    <button className="rounded-[30px] bg-[#43D633] text-sm px-4 py-1 text-white" onClick={activeCard}>
                                                        Activate
                                                    </button>
                                                    {userData.stripe_id && (
                                                        <div className="flex gap-2">
                                                            <button 
                                                                className="rounded-[30px] bg-[#007bff] text-sm px-4 py-1 text-white" 
                                                                onClick={async () => {
                                                                    try {
                                                                        const response = await axios({
                                                                            method: 'post',
                                                                            url: process.env.REACT_APP_PAYMENT_API + '/check-account-active',
                                                                            headers: await getAuthHeaders(),
                                                                            data: { stripeId: userData.stripe_id }
                                                                        });
                                                                        
                                                                        if (response.data.isActive) {
                                                                            await updateUserProfile(userData.id, { stripe_active: true });
                                                                            await getUpdatedUserData();
                                                                            alert('✅ Account is now active!');
                                                                        } else {
                                                                            console.log('Account status:', response.data);
                                                                            alert(`Account not yet active: ${response.data.message || 'Please complete Stripe onboarding'}`);
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Error checking account:', error);
                                                                        alert('Error checking account status');
                                                                    }
                                                                }}
                                                            >
                                                                Check Status
                                                            </button>
                                                            <button 
                                                                className="rounded-[30px] bg-[#ff6b35] text-sm px-4 py-1 text-white" 
                                                                onClick={async () => {
                                                                    try {
                                                                        const response = await axios({
                                                                            method: 'post',
                                                                            url: process.env.REACT_APP_PAYMENT_API + '/update-account-capabilities',
                                                                            headers: await getAuthHeaders(),
                                                                            data: { 
                                                                                stripeId: userData.stripe_id,
                                                                                returnUrl: window.location.href
                                                                            }
                                                                        });
                                                                        
                                                                        if (response.data.success) {
                                                                            alert('Redirecting to Stripe to complete requirements...');
                                                                            window.location.href = response.data.accountLink;
                                                                        } else {
                                                                            alert('Failed to access Stripe dashboard. Please contact support.');
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Error accessing Stripe dashboard:', error);
                                                                        alert('Error accessing Stripe dashboard');
                                                                    }
                                                                }}
                                                            >
                                                                Complete Setup
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-[#43D633] text-sm">
                                                    ✅ Active
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-y-4">
                <div className="border-[1px] border-gray w-full h-fit">
                    <div className="text-[#8F8F8F] uppercase text-xs p-4">Recent Withdraws</div>

                    <table className="text-[#717171] text-xs w-full">
                        <thead className="bg-[#2E2E2E] font-thin text-start">
                            <tr>
                                <th className="text-start pl-4">
                                    Date
                                </th>
                                <th className="text-start">
                                    Withdraw Amount
                                </th>
                                <th className="text-start max-sm:hidden">
                                    Fees
                                </th>
                                <th className="text-start max-sm:hidden">
                                    Total Amount
                                </th>
                                <th className="text-start">
                                    Method
                                </th>
                            </tr>
                        </thead>

                        <tbody className="text-white">
                            {
                                withdraws.slice(5 * (currentPage - 1), 5 * currentPage).map(item =>
                                    <tr key={item.id}>
                                        <td className="pl-4 py-2">
                                            {formatDate(item.createdAt?.seconds)}
                                        </td>
                                        <td className="py-2">
                                            {item.amount}
                                        </td>
                                        <td className="py-2 max-sm:hidden">
                                            0
                                        </td>
                                        <td className="py-2 max-sm:hidden">
                                            {item.amount}
                                        </td>
                                        <td className="py-2 uppercase">
                                            {item.method}
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>

                <Pagination itemsPerPage={5} totalItems={withdraws.length} onPageChange={(v) => { setCurrentPage(v) }} />
            </div>
        </div>
    )
}

export default PaymentSettingsPage
